/*
 * use-sidebar-toggle
 */
import { type StateCreator, useStore } from "zustand"
import {
	createJSONStorage,
	persist,
	type PersistOptions,
	type StateStorage,
} from "zustand/middleware"
import { useShallow } from "zustand/shallow"
import { createStore } from "zustand/vanilla"

const DEFAULT_SIDEBAR_OPEN = true

interface SidebarStoreState {
	isOpen: boolean
	setIsOpen: (open?: boolean) => void
}

type PersistedSidebarState = Pick<SidebarStoreState, "isOpen">

type SidebarStateCreator = StateCreator<
	SidebarStoreState,
	[["zustand/persist", Pick<SidebarStoreState, "isOpen">]]
>

const stateCreatorImpl: SidebarStateCreator = (set, get, _api) => ({
	isOpen: DEFAULT_SIDEBAR_OPEN,

	setIsOpen: (open?: boolean) => {
		const currentState = get()
		const newOpen = open ?? !currentState.isOpen

		if (currentState.isOpen === newOpen) {
			return
		}

		// Update UI immediately for responsive feel
		set({ isOpen: newOpen })
	},
})

const persistOptions: PersistOptions<SidebarStoreState, PersistedSidebarState> =
	{
		name: "sidebar-state",
		storage: createJSONStorage(() => localStorage as StateStorage),
		partialize: (state) => ({
			isOpen: state.isOpen,
		}),
	}

export const sidebarStoreApi = createStore<SidebarStoreState>()(
	persist(stateCreatorImpl as any, persistOptions),
)

export const useSidebarToggle = () => {
	return useStore(
		sidebarStoreApi,
		useShallow((s) => ({
			isOpen: s.isOpen,
			setIsOpen: s.setIsOpen,
		})),
	)
}
